import { Outlet, useLoaderData } from 'react-router-dom';

import RestrictedAccess from '@dmp/components/ui/RestrictedAccess/RestrictedAccess';
import { getInternalAppsPermission, isRbacAdminFromToken, isRbacEnabled, isUploaderFromToken } from '@dmp/utils/rbac';

const NoPermission = <RestrictedAccess />;

export const InternalAppsGuard = () => {
  const permissionKey = useLoaderData();

  const internalAppsPermission = getInternalAppsPermission();
  // JWT dataset_management access === Admin. Uploader/RBAC Administrator are DMP-owned
  // (DB-owned) roles embedded into the JWT at token-mint time as top-level claims
  // (is_uploader/is_rbac_admin) — all three are read synchronously here, no separate
  // GET /api/v1/admin/me/ call needed.
  const isJwtDatasetAdmin = internalAppsPermission.dataset_management;
  const isUploader = isUploaderFromToken();
  // RBAC Administrator is scoped to RBAC management only — it no longer implies access to any
  // internal app.
  const isRbacAdmin = isRbacAdminFromToken();

  switch (permissionKey) {
    case 'dataset_management': {
      // Admins enter immediately; Uploaders enter a scoped view; no_role/RBAC Administrator is denied.
      if (isJwtDatasetAdmin || isUploader) break;
      return NoPermission;
    }

    case 'user_management': {
      // Dataset Management Admin only — assigning Uploaders within their own app.
      if (isJwtDatasetAdmin) break;
      return NoPermission;
    }

    case 'collection_requests': {
      // Platform Admin only — approving/rejecting Collection creation requests (DMP-2370).
      if (isJwtDatasetAdmin) break;
      return NoPermission;
    }

    case 'approval_queue': {
      // Admin only — approving/rejecting Uploader dataset submissions (DMP-2386).
      if (isJwtDatasetAdmin) break;
      return NoPermission;
    }

    case 'my_submissions': {
      // Uploader only — their own review rounds (DMP-2386); Admins use the Approval Queue instead.
      if (isUploader) break;
      return NoPermission;
    }

    case 'rbac': {
      // RBAC Administrator only, mirroring the backend's IsRbacManager permission — a Dataset
      // Management Admin without the RBAC Administrator grant no longer gets in here — and only
      // when the unified RBAC layer is enabled for this tenant.
      if (isRbacEnabled() && isRbacAdmin) break;
      return NoPermission;
    }

    case 'telemetry': {
      if (!internalAppsPermission.telemetry) {
        return NoPermission;
      }
      break;
    }

    case 'analytics': {
      // we don't provide analytics page yet, so we can skip the check
      return <Outlet />;
    }
    case 'portal': {
      // if user does not even have a permission of each internal app, we don't show the portal —
      // unless they hold a DMP role (Uploader, or RBAC Administrator who needs the portal to
      // reach the RBAC card), in which case the portal renders the tiles that role is entitled to.
      if (Object.values(internalAppsPermission).every((permission) => !permission)) {
        if (isUploader || (isRbacEnabled() && isRbacAdmin)) break;
        return NoPermission;
      }
      break;
    }
    default: {
      return <Outlet />;
    }
  }

  return <Outlet />;
};
